import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type GroupByOption,
  getDefaultTaskStatusTab,
  groupTasksForPanel,
  isTerminalStatus,
  matchesTaskTab,
  type Task,
  type TaskGroup,
  type TaskStatusMeta,
  type TimelineFilter,
  tasksAssignedToUser,
  type WorkspaceMember,
} from '../lib/task-list';

const TIMELINE_FILTERS: TimelineFilter[] = ['all', 'overdue', 'today', 'week', 'none'];
const GROUP_BY_OPTIONS: GroupByOption[] = ['none', 'status', 'priority', 'dueDate', 'assignee'];

export type TaskScope = 'all' | 'me' | 'overdue';

function parseIntList(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));
}

function parseStringList(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

function parseTimeline(value: string | null): TimelineFilter {
  return TIMELINE_FILTERS.includes(value as TimelineFilter) ? (value as TimelineFilter) : 'all';
}

function parseGroupBy(value: string | null): GroupByOption {
  return GROUP_BY_OPTIONS.includes(value as GroupByOption) ? (value as GroupByOption) : 'none';
}

function resolveNext<T>(next: React.SetStateAction<T>, prev: T): T {
  return typeof next === 'function' ? (next as (prev: T) => T)(prev) : next;
}

export interface TaskListFilters {
  scope: TaskScope;
  setScope: (scope: TaskScope) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeTab: string;
  setActiveTab: (v: string) => void;
  selectedPriorities: number[];
  setSelectedPriorities: React.Dispatch<React.SetStateAction<number[]>>;
  selectedAssignees: string[];
  setSelectedAssignees: React.Dispatch<React.SetStateAction<string[]>>;
  selectedContactFilter: string;
  setSelectedContactFilter: (v: string) => void;
  selectedTimeline: TimelineFilter;
  setSelectedTimeline: (v: TimelineFilter) => void;
  groupBy: GroupByOption;
  setGroupBy: (v: GroupByOption) => void;
  collapsedGroups: Record<string, boolean>;
  toggleGroup: (key: string) => void;

  /** Tasks in the current view's fixed scope, before the user's optional filters apply. */
  scopedTasks: Task[];
  /** My Tasks fixes the assignee to the signed-in user, so the assignee filter is hidden. */
  isAssigneeFilterLocked: boolean;
  filteredTasks: Task[];
  groupedTasksList: TaskGroup[];
  hasActiveFilters: boolean;
  resetFilters: () => void;
}

/**
 * Search/status-tab/priority/assignee/linked-contact/due-date filter state + status/priority
 * grouping, shared between Skill Viper Portal and clinic-portal's Tasks pages (previously two
 * independently-drifting ~250-line blocks per app). Filter state is URL-backed (`?q=`,
 * `?tab=`, `?priorities=`, `?assignees=`, `?contact=`, `?timeline=`, `?groupBy=`) so a
 * filtered view is shareable via link; `collapsedGroups` stays local UI state.
 */
export function useTaskListFilters(
  tasks: Task[],
  statuses: TaskStatusMeta[],
  currentUserId: number | undefined,
  options: { assigneeScope?: 'all' | 'currentUser'; members?: WorkspaceMember[] } = {}
): TaskListFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAssigneeFilterLocked = options.assigneeScope === 'currentUser';

  const searchQuery = searchParams.get('q') ?? '';
  const defaultStatusTab = getDefaultTaskStatusTab(statuses);
  const requestedStatusTab = searchParams.get('tab');
  const activeTab =
    !requestedStatusTab || requestedStatusTab === 'all' ? defaultStatusTab : requestedStatusTab;
  const selectedPriorities = parseIntList(searchParams.get('priorities'));
  const selectedAssignees = parseStringList(searchParams.get('assignees'));
  const selectedContactFilter = searchParams.get('contact') ?? 'all';
  const selectedTimeline = parseTimeline(searchParams.get('timeline'));
  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>({});

  const rawScope = searchParams.get('scope');
  const scope: TaskScope =
    rawScope === 'me' || rawScope === 'overdue'
      ? rawScope
      : searchParams.get('assignees') === 'me'
        ? 'me'
        : searchParams.get('timeline') === 'overdue'
          ? 'overdue'
          : 'all';

  const setParam = React.useCallback(
    (key: string, value: string | null) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === null || value === '') nextParams.delete(key);
      else nextParams.set(key, value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setScope = React.useCallback(
    (newScope: TaskScope) => {
      const nextParams = new URLSearchParams(searchParams);
      if (newScope === 'all') {
        nextParams.delete('scope');
        if (nextParams.get('assignees') === 'me') {
          nextParams.delete('assignees');
        }
        if (nextParams.get('timeline') === 'overdue') {
          nextParams.delete('timeline');
        }
      } else if (newScope === 'me') {
        nextParams.set('scope', 'me');
        nextParams.set('assignees', 'me');
        if (nextParams.get('timeline') === 'overdue') {
          nextParams.delete('timeline');
        }
      } else if (newScope === 'overdue') {
        nextParams.set('scope', 'overdue');
        nextParams.set('timeline', 'overdue');
        if (nextParams.get('assignees') === 'me') {
          nextParams.delete('assignees');
        }
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setSearchQuery = React.useCallback((v: string) => setParam('q', v), [setParam]);
  const setActiveTab = React.useCallback(
    (v: string) => setParam('tab', v === defaultStatusTab ? null : v),
    [defaultStatusTab, setParam]
  );
  const setSelectedPriorities: React.Dispatch<React.SetStateAction<number[]>> = React.useCallback(
    (next) => {
      const resolved = resolveNext(next, selectedPriorities);
      setParam('priorities', resolved.length ? resolved.join(',') : null);
    },
    [selectedPriorities, setParam]
  );
  const setSelectedAssignees: React.Dispatch<React.SetStateAction<string[]>> = React.useCallback(
    (next) => {
      const resolved = resolveNext(next, selectedAssignees);
      const nextParams = new URLSearchParams(searchParams);
      if (resolved.length) {
        nextParams.set('assignees', resolved.join(','));
        if (resolved.length === 1 && resolved[0] === 'me') {
          nextParams.set('scope', 'me');
        } else {
          nextParams.delete('scope');
        }
      } else {
        nextParams.delete('assignees');
        if (nextParams.get('scope') === 'me') {
          nextParams.delete('scope');
        }
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, selectedAssignees, setSearchParams]
  );
  const setSelectedContactFilter = React.useCallback(
    (v: string) => setParam('contact', v === 'all' ? null : v),
    [setParam]
  );
  const setSelectedTimeline = React.useCallback(
    (v: TimelineFilter) => {
      const nextParams = new URLSearchParams(searchParams);
      if (v === 'all') {
        nextParams.delete('timeline');
        if (nextParams.get('scope') === 'overdue') {
          nextParams.delete('scope');
        }
      } else {
        nextParams.set('timeline', v);
        if (v === 'overdue') {
          nextParams.set('scope', 'overdue');
        } else if (nextParams.get('scope') === 'overdue') {
          nextParams.delete('scope');
        }
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const setGroupBy = React.useCallback(
    (v: GroupByOption) => setParam('groupBy', v === 'none' ? null : v),
    [setParam]
  );

  const toggleGroup = React.useCallback((key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const isTerminal = React.useCallback(
    (statusKey: string | null) => isTerminalStatus(statuses, statusKey),
    [statuses]
  );

  const scopedTasks = React.useMemo(
    () => (isAssigneeFilterLocked ? tasksAssignedToUser(tasks, currentUserId) : tasks),
    [tasks, currentUserId, isAssigneeFilterLocked]
  );

  const filteredTasks = React.useMemo(() => {
    return scopedTasks.filter((task) => {
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const labelMatch = task.label?.toLowerCase().includes(query);
        const contentMatch = task.content?.toLowerCase().includes(query);
        if (!labelMatch && !contentMatch) return false;
      }

      const status = task.status || 'todo';
      if (!matchesTaskTab(statuses, task, activeTab)) return false;

      if (selectedPriorities.length > 0) {
        const priorityVal = task.priority ?? 0;
        if (!selectedPriorities.includes(priorityVal)) return false;
      }

      if (!isAssigneeFilterLocked && selectedAssignees.length > 0) {
        const assignee = task.assignedTo;
        const matches = selectedAssignees.some((filterVal) => {
          if (filterVal === 'unassigned') return !assignee;
          if (filterVal === 'me') return assignee === currentUserId;
          return assignee === Number(filterVal);
        });
        if (!matches) return false;
      }

      if (selectedContactFilter !== 'all') {
        if (selectedContactFilter === 'standalone') {
          if (task.contactId !== null && task.contactId !== undefined) return false;
        } else if (selectedContactFilter === 'linked') {
          if (task.contactId === null || task.contactId === undefined) return false;
        } else {
          if (task.contactId?.toString() !== selectedContactFilter) return false;
        }
      }

      if (selectedTimeline !== 'all') {
        const nowSec = Math.floor(Date.now() / 1000);
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const startOfDaySec = Math.floor(startOfDay.getTime() / 1000);
        const endOfDaySec = startOfDaySec + 86400;
        const endOfWeekSec = startOfDaySec + 86400 * 7;

        if (selectedTimeline === 'overdue') {
          if (!task.dueAt || task.dueAt >= nowSec || isTerminal(status)) return false;
        } else if (selectedTimeline === 'today') {
          if (!task.dueAt || task.dueAt < startOfDaySec || task.dueAt >= endOfDaySec) return false;
        } else if (selectedTimeline === 'week') {
          if (!task.dueAt || task.dueAt < startOfDaySec || task.dueAt >= endOfWeekSec) return false;
        } else if (selectedTimeline === 'none') {
          if (task.dueAt) return false;
        }
      }

      return true;
    });
  }, [
    scopedTasks,
    searchQuery,
    activeTab,
    statuses,
    selectedPriorities,
    selectedAssignees,
    selectedContactFilter,
    selectedTimeline,
    isTerminal,
    currentUserId,
    isAssigneeFilterLocked,
  ]);

  // Bucketing lives in `groupTasksForPanel` so panels — which have no URL state — can reuse it.
  // It used to be inline here, which is why each panel wrote its own and they disagreed.
  const groupedTasksList = React.useMemo(
    (): TaskGroup[] =>
      groupTasksForPanel(filteredTasks, statuses, groupBy, {
        activeTab,
        members: options.members,
      }),
    [filteredTasks, groupBy, activeTab, statuses, options.members]
  );

  const hasActiveFilters =
    scope !== 'all' ||
    selectedPriorities.length > 0 ||
    (!isAssigneeFilterLocked && selectedAssignees.length > 0) ||
    selectedContactFilter !== 'all' ||
    selectedTimeline !== 'all' ||
    searchQuery.trim() !== '';

  const resetFilters = React.useCallback(() => {
    // Deletes all keys in one update rather than calling each setter above — those each
    // build their `nextParams` off the same render's `searchParams` snapshot, so five
    // back-to-back calls would clobber each other and only the last one would stick.
    const nextParams = new URLSearchParams(searchParams);
    for (const key of ['q', 'priorities', 'assignees', 'contact', 'timeline', 'scope']) {
      nextParams.delete(key);
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    scope,
    setScope,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    selectedPriorities,
    setSelectedPriorities,
    selectedAssignees,
    setSelectedAssignees,
    selectedContactFilter,
    setSelectedContactFilter,
    selectedTimeline,
    setSelectedTimeline,
    groupBy,
    setGroupBy,
    collapsedGroups,
    toggleGroup,
    scopedTasks,
    isAssigneeFilterLocked,
    filteredTasks,
    groupedTasksList,
    hasActiveFilters,
    resetFilters,
  };
}
